import { assetManifest } from "../../assets/manifest";
import type { CosmeticItemView } from "../../game/cosmetics/view";
import type { GameSnapshot } from "../../game/runtime/types";
import type { OrbitForgeRuntime } from "../../game/runtime/runtime";

export function createCosmeticsPanel(runtime: OrbitForgeRuntime): HTMLElement {
  const panel = document.createElement("section");
  panel.className = "of-panel of-cosmetics-panel";
  panel.setAttribute("aria-label", "Cosmetic selector");

  let renderedSignature = "";
  let seenFeedbackId = 0;
  let wasOpen = false;

  runtime.subscribe((snapshot) => {
    const isOpen = snapshot.activePanel === "settings";
    if (isOpen !== wasOpen) {
      panel.classList.toggle("is-open", isOpen);
      wasOpen = isOpen;
    }
    if (!isOpen) {
      return;
    }

    const signature = cosmeticsSignature(snapshot);
    if (signature !== renderedSignature) {
      renderPanel(panel, snapshot, runtime);
      renderedSignature = signature;
    }

    seenFeedbackId = applyCosmeticFeedback(panel, snapshot, seenFeedbackId);
  });

  return panel;
}

function cosmeticsSignature(snapshot: GameSnapshot): string {
  return [
    snapshot.activePanel,
    snapshot.progress.effectiveLevel,
    snapshot.progress.prestigeLevel,
    snapshot.save.cosmetics.SelectedShellSkin,
    snapshot.save.cosmetics.SelectedCorePattern,
    snapshot.save.cosmetics.UnlockedCrateShellSkins.join(","),
    snapshot.save.cosmetics.UnlockedCrateCorePatterns.join(",")
  ].join("|");
}

function renderPanel(panel: HTMLElement, snapshot: GameSnapshot, runtime: OrbitForgeRuntime): void {
  const title = document.createElement("h2");
  title.className = "of-panel-title";
  title.textContent = "COSMETICS";

  const count = document.createElement("span");
  count.className = "of-cosmetic-count";
  count.textContent = `${snapshot.cosmetics.unlockedCount}/${snapshot.cosmetics.totalCount}`;

  const header = document.createElement("div");
  header.className = "of-panel-header of-cosmetics-header";
  header.append(title, count);

  const selected = document.createElement("div");
  selected.className = "of-cosmetic-selected";
  selected.append(
    selectedChip("SHELL", snapshot.cosmetics.selectedShellSkinLabel),
    selectedChip("CORE", snapshot.cosmetics.selectedCorePatternLabel)
  );

  const body = document.createElement("div");
  body.className = "of-cosmetic-body";
  body.append(
    createCosmeticSection("SHELL SKINS", snapshot.cosmetics.shellSkins, runtime),
    createCosmeticSection("CORE PATTERNS", snapshot.cosmetics.corePatterns, runtime)
  );

  panel.replaceChildren(header, selected, body);
}

function createCosmeticSection(title: string, items: readonly CosmeticItemView[], runtime: OrbitForgeRuntime): HTMLElement {
  const section = document.createElement("section");
  section.className = "of-cosmetic-section";
  section.setAttribute("aria-label", title);

  const heading = document.createElement("h3");
  heading.className = "of-cosmetic-section-title";
  heading.textContent = title;

  const list = document.createElement("div");
  list.className = "of-cosmetic-list";
  list.append(...items.map((item) => createCosmeticItem(item, runtime)));

  section.append(heading, list);
  return section;
}

function createCosmeticItem(item: CosmeticItemView, runtime: OrbitForgeRuntime): HTMLButtonElement {
  const button = document.createElement("button");
  button.className = "of-cosmetic-item";
  button.type = "button";
  button.dataset.cosmeticKind = item.kind;
  button.dataset.cosmeticId = item.id;
  button.dataset.cosmeticTarget = item.feedbackTarget;
  button.classList.toggle("is-selected", item.selected);
  button.classList.toggle("is-locked", !item.unlocked);
  button.classList.toggle("is-crate", item.source === "crate");
  button.classList.toggle("is-prestige", item.source === "prestige");
  button.setAttribute("aria-disabled", item.unlocked ? "false" : "true");
  button.setAttribute("aria-label", item.unlocked ? `${item.label}, ${item.stateLabel}` : `${item.label}, ${item.unlockLabel}`);
  button.addEventListener("click", () => runtime.dispatch({ type: "equip-cosmetic", kind: item.kind, id: item.id }));

  const details = document.createElement("span");
  details.className = "of-cosmetic-details";
  details.append(textSpan(item.label, "of-cosmetic-name"), textSpan(item.unlockLabel, "of-cosmetic-source"));

  button.append(createPreview(item), details, textSpan(item.stateLabel, "of-cosmetic-state"));
  return button;
}

function createPreview(item: CosmeticItemView): HTMLElement {
  const preview = document.createElement("span");
  preview.className = "of-cosmetic-preview";
  preview.dataset.cosmeticKind = item.kind;
  preview.dataset.cosmeticId = item.id;

  if (item.assetKey) {
    const image = document.createElement("img");
    image.className = "of-cosmetic-art";
    image.src = assetManifest[item.assetKey].url;
    image.alt = "";
    preview.append(image);
    return preview;
  }

  if (item.kind === "shellSkin") {
    preview.append(shellRing("outer"), shellRing("middle"), shellRing("inner"));
  } else {
    const core = document.createElement("span");
    core.className = "of-cosmetic-core-preview";
    preview.append(core);
  }

  return preview;
}

function shellRing(name: string): HTMLElement {
  const ring = document.createElement("span");
  ring.className = `of-cosmetic-shell-ring of-cosmetic-shell-${name}`;
  return ring;
}

function selectedChip(label: string, value: string): HTMLElement {
  const chip = document.createElement("span");
  chip.className = "of-cosmetic-selected-chip";
  chip.append(textSpan(label, "of-cosmetic-selected-label"), textSpan(value, "of-cosmetic-selected-value"));
  return chip;
}

function applyCosmeticFeedback(panel: HTMLElement, snapshot: GameSnapshot, seenFeedbackId: number): number {
  let highestFeedbackId = seenFeedbackId;
  for (const event of snapshot.feedback) {
    if (event.id <= seenFeedbackId) {
      continue;
    }

    highestFeedbackId = Math.max(highestFeedbackId, event.id);
    if (!event.target?.startsWith("cosmetic-")) {
      continue;
    }

    const item = panel.querySelector<HTMLElement>(`.of-cosmetic-item[data-cosmetic-target="${event.target}"]`);
    if (!item) {
      continue;
    }

    const className = event.kind === "denied" ? "is-denied" : "is-pulsing";
    item.classList.add(className);
    window.setTimeout(() => item.classList.remove(className), 320);
  }
  return highestFeedbackId;
}

function textSpan(text: string, className: string): HTMLElement {
  const span = document.createElement("span");
  span.className = className;
  span.textContent = text;
  return span;
}
